import React from 'react'

import { ZariContext } from '_zari/context'

import { Table, Portlet, Form, Col, Row, Button } from '_zari/core'

import * as CodigosTransaccionesConstant from 'constants/codigosTransacciones'
import TiposDeProductoConstant from 'constants/tiposDeProducto'

import * as ProductoService from 'services/producto.service'
import * as CompaniaService from 'services/compania.service'
import * as CategoriaService from 'services/categoria.service'
import * as FamiliaService from 'services/familia.service'
import * as SubFamiliaService from 'services/subFamilia.service'

import ImportarExcelModal from './modals/importarExcel'
import GenerarCopiaModal from './modals/generarCopia'

import { ZrSwal, DownloadAxiosFile } from 'utils'

const portletBreadcrumbs = [{ name: 'Productos' }]

const tableHeaderRows = [
  {
    label: 'CÓDIGO',
    width: 110,
    filter: {
      type: 'text',
      name: 'cod'
    }
  },
  {
    label: 'NOMBRE',
    filter: {
      type: 'text',
      name: 'nom'
    }
  },
  { label: 'CATEGORÍA' },
  {
    label: 'CTA. VENTA',
    filter: {
      type: 'text',
      name: 'ctavta'
    },
    width: 90
  },
  {
    label: 'CTA. COMPRA',
    filter: {
      type: 'text',
      name: 'ctacmp'
    },
    width: 90
  },
  { label: 'TIPO', width: 90 },
  { label: '', width: 40 }
]

const ListaProductosPage = ({ history }) => {
  const __zariContext = React.useContext(ZariContext)

  const tablaRef = React.useRef(null)
  const copiaData = React.useRef({})

  const [mostrarGenerarCopiaModal, setMostrarGenerarCopiaModal] = React.useState(false)
  const [mostrarImportarExcelModal, setMostrarImportarExcelModal] = React.useState(false)
  const [flagCompaniaParaProductos, setFlagCompaniaParaProducto] = React.useState(false)

  // Filtros
  const [tipo, setTipo] = React.useState('')
  const [idCategoria, setIdCategoria] = React.useState('')
  const [idFamilia, setIdFamilia] = React.useState('')
  const [idSubFamilia, setIdSubFamilia] = React.useState('')

  const [categorias, setCategorias] = React.useState([])
  const [familias, setFamilias] = React.useState([])
  const [subfamilias, setSubfamilias] = React.useState([])

  React.useEffect(() => {
    CompaniaService.self.validarCompaniaProducto().then(({ data }) => {
      setFlagCompaniaParaProducto(data.esValido)
    })
    CategoriaService.obtenerAsSelect()
      .then(({ data }) => setCategorias(data))
      .catch(() => {})
  }, [])

  React.useEffect(() => {
    if (idCategoria) {
      FamiliaService.getFamilias_Select(idCategoria)
        .then(({ data }) => setFamilias(data))
        .catch(() => {})
    } else {
      setFamilias([])
    }
    setIdFamilia('')
    setIdSubFamilia('')
  }, [idCategoria])

  React.useEffect(() => {
    if (idFamilia) {
      SubFamiliaService.getSubFamilias_Select(idFamilia)
        .then(({ data }) => setSubfamilias(data))
        .catch(() => {})
    } else {
      setSubfamilias([])
    }
    setIdSubFamilia('')
  }, [idFamilia])

  const clickBuscar = () => {
    tablaRef.current.reload({
      tipo,
      idCategoria,
      idFamilia,
      idSubFamilia
    })
  }

  const clickLimpiar = () => {
    setTipo('')
    setIdCategoria('')
    setIdFamilia('')
    setIdSubFamilia('')
    tablaRef.current.reload({})
  }

  const clickImportarExcelHandler = React.useCallback(() => {
    setMostrarImportarExcelModal(true)
  }, [])

  const clickDescargarInforme = React.useCallback(() => {
    __zariContext.loader.open('Descargando informe de productos')
    ProductoService.descargarInformeProductos()
      .then(({ data, headers }) => {
        DownloadAxiosFile.downloadAsBlob(data, headers)
      })
      .finally(() => {
        __zariContext.loader.close()
      })
  }, [__zariContext])

  const clickEditar = React.useCallback(id => history.push(`/${CodigosTransaccionesConstant.PRODUCTOS_NUEVO}/${id}`), [
    history
  ])

  const clickNuevo = React.useCallback(() => history.push(`/${CodigosTransaccionesConstant.PRODUCTOS_NUEVO}`), [
    history
  ])

  const clickGenerarCopia = React.useCallback(({ _id, nombre }) => {
    copiaData.current = {
      _id,
      nombre
    }

    setMostrarGenerarCopiaModal(true)
  }, [])

  const clickEliminar = React.useCallback(
    row => {
      ZrSwal.showAlert({
        tipo: 'warning',
        mensaje: `Seguro de eliminar el producto ${row.nombre} ?`,
        showCerrar: true,
        onSuccess: () => {
          ProductoService.eliminarProducto(row._id)
            .then(() => {
              __zariContext.toast('success', 'Registro eliminado.')
              tablaRef.current.reload()
            })
            .catch(({ data: { error } }) => __zariContext.snack('error', error))
        }
      })
    },
    [__zariContext]
  )

  return (
    <>
      {flagCompaniaParaProductos && (
        <>
          <Portlet>
            <Portlet.Header
              title='Productos'
              breadcrumbs={portletBreadcrumbs}
              buttons={[
                { name: 'Nuevo', onClick: clickNuevo },
                { name: 'Descargar informe', onClick: clickDescargarInforme },
                { name: 'Importar Productos', onClick: clickImportarExcelHandler }
              ]}
            />
            <Portlet.Body>
              <Row className='p-3'>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label text='TIPO' htmlFor='filtro-tipo' md={4} />
                    <Col md={8}>
                      <Form.Select id='filtro-tipo' value={tipo} onChange={e => setTipo(e.target.value)}>
                        <option value=''>Todos</option>
                        {TiposDeProductoConstant.__array.map(t => (
                          <option key={t.id} value={t.id}>
                            {t.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label text='CATEGORÍA' htmlFor='filtro-categoria' md={5} />
                    <Col md={7}>
                      <Form.Select id='filtro-categoria' value={idCategoria} onChange={e => setIdCategoria(e.target.value)}>
                        <option value=''>Todos</option>
                        {categorias.map(c => (
                          <option key={c._id} value={c._id}>
                            {c.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label text='FAMILIA' htmlFor='filtro-familia' md={5} />
                    <Col md={7}>
                      <Form.Select
                        id='filtro-familia'
                        value={idFamilia}
                        onChange={e => setIdFamilia(e.target.value)}
                        disabled={!idCategoria}
                      >
                        <option value=''>Todos</option>
                        {familias.map(f => (
                          <option key={f._id} value={f._id}>
                            {f.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label text='SUBFAMILIA' htmlFor='filtro-subfamilia' md={5} />
                    <Col md={7}>
                      <Form.Select
                        id='filtro-subfamilia'
                        value={idSubFamilia}
                        onChange={e => setIdSubFamilia(e.target.value)}
                        disabled={!idFamilia}
                      >
                        <option value=''>Todos</option>
                        {subfamilias.map(sf => (
                          <option key={sf._id} value={sf._id}>
                            {sf.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>
              <Row className='px-3 pb-3'>
                <Col md={12} className='d-flex justify-content-end'>
                  <Button variant='secondary' className='mr-2' onClick={clickLimpiar}>
                    Limpiar
                  </Button>
                  <Button onClick={clickBuscar}>Buscar</Button>
                </Col>
              </Row>
              <Table.ServerSide
                ref={tablaRef}
                useFilters={true}
                useNest={true}
                url='/productos'
                headRows={tableHeaderRows}
                rowTemplate={row => (
                  <Table.Row hover key={row._id}>
                    <Table.Cell>{row.codigo || '--'}</Table.Cell>
                    <Table.Cell>{row.nombre || '--'}</Table.Cell>
                    <Table.Cell>{row.categoria || '--'}</Table.Cell>
                    <Table.Cell>{row.cuentaContableVenta || '--'}</Table.Cell>
                    <Table.Cell>{row.cuentaContableCompra || '--'}</Table.Cell>
                    <Table.Cell>{row.tipo ? TiposDeProductoConstant.__dictionary[row.tipo].nombre : '--'}</Table.Cell>
                    <Table.Cell>
                      <Table.Dropdown>
                        {({ handleClose }) => [
                          <Table.Dropdown.Item
                            key='editar'
                            handleClose={handleClose}
                            onClick={e => clickEditar(row._id)}
                          >
                            Editar
                          </Table.Dropdown.Item>,
                          <Table.Dropdown.Item
                            key='eliminar'
                            handleClose={handleClose}
                            onClick={e => clickEliminar(row)}
                          >
                            Eliminar
                          </Table.Dropdown.Item>,
                          <Table.Dropdown.Divider key='divider-1' />,
                          <Table.Dropdown.Item
                            key='generar-copia'
                            handleClose={handleClose}
                            onClick={e =>
                              clickGenerarCopia({
                                _id: row._id,
                                nombre: row.nombre
                              })
                            }
                          >
                            Generar Copia
                          </Table.Dropdown.Item>
                        ]}
                      </Table.Dropdown>
                    </Table.Cell>
                  </Table.Row>
                )}
              />
            </Portlet.Body>
          </Portlet>
          {mostrarGenerarCopiaModal && (
            <GenerarCopiaModal
              handleClose={e => setMostrarGenerarCopiaModal(false)}
              show={mostrarGenerarCopiaModal}
              modalData={copiaData.current}
              reloadTable={tablaRef.current.reload}
            />
          )}
          {mostrarImportarExcelModal && (
            <ImportarExcelModal
              handleClose={e => setMostrarImportarExcelModal(false)}
              show={mostrarImportarExcelModal}
              reloadTable={tablaRef.current.reload}
            />
          )}
        </>
      )}
    </>
  )
}

export default ListaProductosPage
